/**
 * Links from the graph back into the Mobile Forensics reader.
 *
 * A promoted excerpt is a node, but the evidence is the thread it was taken
 * from. Copying the text onto the node (its TEXT property) is not a way back:
 * the analyst has to land in the reader on that conversation, at that range,
 * with that excerpt in focus. The reader consumes these query parameters when
 * it opens (see `CommsPage`), so anything that knows a conversation — an
 * excerpt node, a Conversation node, an agent's window hit — can point there.
 *
 * Pure builders only; no router, no data.
 */

export interface CommsReaderTarget {
  caseId: string
  /** Pins the device extraction — a case may hold several, and the reader
   * would otherwise open the newest one. */
  extractionId?: string | null
  conversationId: string
  /** Open the thread at this instant instead of at its newest page. */
  from?: string | null
  /** Scroll to this promoted range and keep it highlighted. */
  excerptId?: string | null
}

export function commsReaderLink(target: CommsReaderTarget): string {
  const params = new URLSearchParams({ conversation: target.conversationId })
  if (target.extractionId) params.set('extraction', target.extractionId)
  if (target.from) params.set('from', target.from)
  if (target.excerptId) params.set('excerpt', target.excerptId)
  return `/cases/${target.caseId}/comms?${params.toString()}`
}

/** A property value as a non-empty string, else undefined. */
function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

/** An instant the reader can anchor on — anything else would 422 upstream. */
function instant(value: unknown): string | undefined {
  const candidate = text(value)
  return candidate && !Number.isNaN(Date.parse(candidate)) ? candidate : undefined
}

/**
 * The reader link for a `MessageExcerpt` node, from the properties promotion
 * stamped on it. Null when they are missing — a node that merely looks like an
 * excerpt gets no link rather than a broken one.
 *
 * The anchor is the range's **end**: the reader's page is the messages up to
 * the anchor (`before` is exclusive, the hook nudges it past the anchored
 * message) and nothing newer loads, so anchoring on the start would leave every
 * message after the first one outside the page. Anchored on the end, the whole
 * range is in the page and the reader scrolls back to its first message.
 */
export function excerptReaderLink(
  caseId: string,
  properties: Record<string, unknown>
): string | null {
  const conversationId = text(properties.conversation_id)
  const excerptId = text(properties.excerpt_id)
  if (!conversationId || !excerptId) return null
  return commsReaderLink({
    caseId,
    conversationId,
    excerptId,
    extractionId: text(properties.comms_extraction_id),
    from: instant(properties.ts_to) ?? instant(properties.ts_from),
  })
}

/**
 * The reader link for a `Conversation` node: the thread, newest page.
 *
 * The skeleton projection stamps the device extraction as
 * `_comms_extraction_id`; the node's plain `extraction_id` is the ingest run,
 * a different id the reader would not recognise, so it is deliberately not a
 * fallback.
 */
export function conversationReaderLink(
  caseId: string,
  properties: Record<string, unknown>
): string | null {
  const conversationId = text(properties.conversation_id)
  if (!conversationId) return null
  return commsReaderLink({
    caseId,
    conversationId,
    extractionId: text(properties.comms_extraction_id) ?? text(properties._comms_extraction_id),
  })
}
